import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { amount, dateLabel, money, useBooks } from "@/lib/books";
import { Input } from "@/components/ui/input";
import { DataTable, Loaded, PageHeader, PeriodFilter } from "@/components/books/ui";
import { usePeriod } from "@/components/books/Statement";

/** Stock items with Tally's own closing quantity and value. */
export function Items() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const state = useBooks("/books/items");
  const needle = q.trim().toLowerCase();
  const rows = (state.data?.rows || []).filter((r) => !needle || r.name.toLowerCase().includes(needle)
    || (r.hsn || "").toLowerCase().includes(needle) || (r.group || "").toLowerCase().includes(needle));
  const snap = state.data?.snapshot;
  return (
    <div>
      <PageHeader kicker="Items" title="All Items"
        subtitle={snap?.as_of ? `Stock as valued by Tally on ${dateLabel(snap.as_of)} · total ${money(state.data.total)}`
          : snap?.source === "missing" ? "Tally has not reported stock values yet" : null}>
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items" className="w-64 pl-8" />
        </div>
      </PageHeader>
      <Loaded state={state}>
        {state.data && (
          <DataTable rows={rows} onRowClick={(r) => navigate(`/items/${r.guid}`)} empty="No stock items in Tally."
            columns={[
              { key: "name", label: "Name", render: (r) => <span className="font-medium text-blue-700">{r.name}</span> },
              { key: "group", label: "Stock group", render: (r) => r.group || "—" },
              { key: "hsn", label: "HSN / SAC", render: (r) => <span className="font-mono text-xs">{r.hsn || "—"}</span> },
              { key: "gst", label: "GST", align: "right", render: (r) => (r.gst_rate ? `${r.gst_rate}%` : "—") },
              { key: "qty", label: "Stock on hand", align: "right", render: (r) => `${amount(r.qty).replace(/\.00$/, "")} ${r.unit || ""}` },
              { key: "rate", label: "Rate", align: "right", render: (r) => amount(r.rate) },
              { key: "value", label: "Value", align: "right", render: (r) => money(r.value) },
            ]} />
        )}
      </Loaded>
    </div>
  );
}

/** One item: its details and every movement in the period. */
export function ItemView() {
  const { guid } = useParams();
  const navigate = useNavigate();
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const state = useBooks(`/books/items/${guid}`, { date_from: period.from, date_to: period.to });
  return (
    <div className="max-w-6xl">
      <Loaded state={state}>
        {state.data && (
          <>
            <PageHeader kicker={state.data.parent || "Items"} title={state.data.name}
              subtitle={[state.data.hsn && `HSN ${state.data.hsn}`, state.data.gst_rate && `GST ${state.data.gst_rate}%`,
                state.data.base_units && `Unit ${state.data.base_units}`].filter(Boolean).join(" · ")} />
            <div className="mb-3"><PeriodFilter value={period} onChange={setPeriod} fyMonth={fyMonth} /></div>
            <DataTable rows={state.data.movements} rowKey={(m) => `${m.voucher_guid}-${m.date}-${m.inward}-${m.outward}`}
              onRowClick={(m) => navigate(`/vouchers/${m.voucher_guid}`)} empty="No movement in this period."
              columns={[
                { key: "date", label: "Date", render: (m) => dateLabel(m.date), className: "whitespace-nowrap" },
                { key: "party", label: "Particulars", render: (m) => m.party || "—" },
                { key: "voucher_type", label: "Vch type" },
                { key: "number", label: "Vch no." },
                { key: "inward", label: "Inward", align: "right", render: (m) => (m.inward ? `${m.inward} ${m.unit || ""}` : "") },
                { key: "outward", label: "Outward", align: "right", render: (m) => (m.outward ? `${m.outward} ${m.unit || ""}` : "") },
                { key: "rate", label: "Rate", align: "right", render: (m) => amount(m.rate) },
                { key: "amount", label: "Value", align: "right", render: (m) => amount(m.amount) },
              ]} />
          </>
        )}
      </Loaded>
    </div>
  );
}
